import { ExclamationCircleFilled, InfoCircleFilled } from '@ant-design/icons';
import * as React from 'react';
import { cn } from '../../../utils/ClassNamesUtils';
import { humanizeTurnStatusText } from '../../../utils/AgentErrorUtils';
import { AgentTurnStatusOutcome, AgentTurnWrites } from '../../../types/agentHarness';

interface OscarTurnStatusBlockProps {
	outcome: AgentTurnStatusOutcome;
	/** The harness's own sentence, counted from its action journal. */
	text: string;
	writes?: AgentTurnWrites;
	className?: string;
}

const HEADINGS: Record<AgentTurnStatusOutcome, string> = {
	stopped: 'Oscar stopped',
	silent: 'No answer',
	partial: 'Partial answer',
	incomplete: 'Not every change ran',
};

/** "6 ran · 18 not run · 1 unknown" — the counts at a glance; the text says why. */
export const describeTurnWrites = (writes: AgentTurnWrites | undefined): string => {
	if (!writes || !writes.proposed) {
		return '';
	}
	return [
		`${writes.ran} of ${writes.proposed} ran`,
		writes.notRun ? `${writes.notRun} not run` : '',
		writes.unknown ? `${writes.unknown} unknown` : '',
	]
		.filter(Boolean)
		.join(' · ');
};

/**
 * How a turn ended, when the answer alone does not say: it stopped, went
 * silent, answered on its last allowed step, or left changes unrun.
 *
 * This used to be a small red footnote under the stop reason, below a stack of
 * approval cards — a customer who asked for 24 tickets could not tell from it
 * whether any were made. The harness now counts the writes from its journal,
 * never from what the model claims, and this puts that count where the turn
 * ends, in a block that reads as the turn's outcome.
 */
export const OscarTurnStatusBlock = ({
	outcome,
	text,
	writes,
	className,
}: OscarTurnStatusBlockProps) => {
	const warning = outcome === 'stopped' || outcome === 'incomplete';
	const counts = describeTurnWrites(writes);
	return (
		<div
			role="status"
			data-testid="oscar-turn-status"
			className={cn(
				'flex w-full min-w-0 items-start gap-s rounded border border-solid p-xs',
				warning ? 'border-sunsetOrange-8 bg-calendulaGold-1' : 'border-grey-5 bg-grey-2',
				className
			)}
		>
			<span
				aria-hidden="true"
				className={cn(
					'shrink-0 text-[18px]',
					warning ? 'text-sunsetOrange-8' : 'text-character-secondary'
				)}
			>
				{warning ? <ExclamationCircleFilled /> : <InfoCircleFilled />}
			</span>
			<span className="flex min-w-0 flex-1 flex-col gap-2xs">
				<span className="bodymedium text-character-primary">{HEADINGS[outcome] || 'Status'}</span>
				<span className="bodyregular whitespace-pre-wrap break-words text-grey-9">
					{humanizeTurnStatusText(text)}
				</span>
				{counts ? <span className="footnoteregular text-character-secondary">{counts}</span> : null}
			</span>
		</div>
	);
};

export default OscarTurnStatusBlock;
